import { useRef, useEffect } from 'react'

import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'

import { convertTraits } from '@aytm/app/stats/lib/query/traits'
import { getRejectsAndKudos } from '@aytm/app/stats/page/comments/lib/utils'
import { AbortController as _abortControllerPolyfill } from 'abortcontroller-polyfill/dist/cjs-ponyfill'
import { DEFAULT_FETCH_TIMEOUT, FETCH_TIMEOUT_STEP } from '@aytm/app/stats/page/comments/lib/constants'
import { fetchSearchResults } from '@aytm/app/stats/page/comments/apiCalls'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

/* compat/compat disabled because polyfill is used */
const AbortController = window.AbortController || _abortControllerPolyfill

let fetchTimeout = DEFAULT_FETCH_TIMEOUT
let fetchDebounceTimeout = 0
let fetchTimer = null

const useFetchDataImageResponses = ({
  apiKey,
  dispatch,
  dispatchCommentStatuses,
  searchStateRef,
  surveyData,
  setSurvey,
  specialMode,
  onFetchSchema,
  onFetch,
  onFetchApiKey
}) => {
  const abortController = useRef(null)

  const { isPreserveState, request } = searchStateRef.current
  const { query, selectedQuestion, pageSize, orderBy, orderDirection, currentPage } = request

  const fetchDataCallback = async data => {
    const { cs } = data

    dispatch({ type: 'SET_PARAM', request: { name: 'isProcessing', value: true } })

    const _survey = data.survey || surveyData.survey || {}

    const assets = data.assets || _survey.assets || {}
    const traits = convertTraits(_survey)

    let codeBooks = _survey.codeBooks
    if (!Array.isArray(codeBooks)) {
      codeBooks = Object.keys(codeBooks || {}).reduce(
        (acc, cur) => acc.concat(codeBooks[cur].map(codebook => Object.assign({ type: cur }, codebook))),
        []
      )
    }

    await setSurvey(
      Object.assign({}, _survey, {
        assets,
        traits,
        codeBooks
      })
    )

    dispatch({ type: 'SEARCH_RESULTS', data: { data, specialMode, pageSize } })
    dispatchCommentStatuses({
      type: 'BATCH_DATA',
      data: { batchData: getRejectsAndKudos(cs), isPreserveState }
    })
    dispatch({ type: 'SET_PARAM', request: { name: 'isPreserveState', value: false } })

    fetchTimeout = DEFAULT_FETCH_TIMEOUT
  }

  const fetchData = async (
    { includeSchema = false, customParams = {}, customFetchDataCallback = null } = {},
    fetchDataComplete = null
  ) => {
    try {
      if (null !== abortController.current) {
        abortController.current.abort()
        abortController.current = null
      }

      abortController.current = new AbortController()

      const { signal } = abortController.current
      const data = await fetchSearchResults({
        query,
        selectedQuestion,
        currentPage,
        pageSize,
        orderBy,
        orderDirection,
        flattenAnswers: true,
        includeSchema: includeSchema || (!customFetchDataCallback && surveyData.survey == undefined),
        signal,
        ...customParams
      })

      const callback = _data => {
        dispatch({ type: 'SET_PARAM', request: { name: 'isLoading', value: false } })

        fetchDataCallback(_data)
        if (fetchDataComplete && 'function' == typeof fetchDataComplete) {
          fetchDataComplete()
        }
      }

      switch (data.status) {
        case 'OK': {
          if (null !== customFetchDataCallback) {
            dispatch({ type: 'SET_PARAM', request: { name: 'isLoading', value: false } })

            customFetchDataCallback(data)
          } else {
            if (
              PageGlobals.SHARE_KEY == undefined &&
              data.survey != undefined &&
              data.survey.target_market.quotas == undefined
            ) {
              if (onFetchSchema && 'function' == typeof onFetchSchema) {
                onFetchSchema(callback, data)
              }
            } else {
              callback(data)

              if (onFetch && 'function' == typeof onFetch) {
                onFetch(data)
              }
            }
          }
          break
        }
        case 'RUNNING': {
          fetchTimeout += FETCH_TIMEOUT_STEP
          fetchTimer = setTimeout(() => {
            fetchTimer = null
            fetchData({ includeSchema, customParams, customFetchDataCallback }, fetchDataComplete)
          }, fetchTimeout)
          break
        }
        case 'NO SCHEMA': {
          if (onFetchSchema && 'function' == typeof onFetchSchema) {
            onFetchSchema(callback, data)
          }
          break
        }
        case 'INVALID KEY': {
          if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
            onFetchApiKey(fetchDataComplete)
          }
          break
        }
        default: {
          dispatch({ type: 'FAILURE', error: { name: 'CustomError', message: 'Something happen' } })
        }
      }
      fetchDebounceTimeout = 0

      abortController.current = null
    } catch (error) {
      if (!error.name || (error.name && 'AbortError' !== error.name)) {
        dispatch({ type: 'FAILURE', error })
      }

      fetchTimeout = DEFAULT_FETCH_TIMEOUT
      fetchDebounceTimeout = 0
    }
  }

  const fetchDataDelay = (force = false, fetchDataComplete) => {
    dispatch({ type: 'SEARCH_INIT' })

    fetchTimer && clearTimeout(fetchTimer)

    if (null !== abortController.current) {
      abortController.current.abort()
      abortController.current = null
    }

    const callback = () => {
      fetchTimer = null

      if (apiKey == undefined || null === apiKey) {
        // fetchDataCallback(getDefaultData())
        return
      }

      fetchData({ includeSchema: force }, fetchDataComplete)
    }

    if (0 < fetchDebounceTimeout) {
      fetchTimer = setTimeout(callback, fetchDebounceTimeout)
    } else callback()
  }

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const fetchDataDestroy = () => {
    fetchTimeout = DEFAULT_FETCH_TIMEOUT
    fetchDebounceTimeout = 1000

    fetchTimer && clearTimeout(fetchTimer)

    if (null !== abortController.current) {
      abortController.current?.abort()
      abortController.current = null
    }
  }

  // normal mode fetch data effect
  useEffect(() => {
    fetchDataDelay(true)

    return () => fetchDataDestroy()
    // disabled: fetchDataDelay is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey])

  // normal mode fetch data effect
  useNonInitialEffect(() => {
    fetchDataDelay()

    return () => fetchDataDestroy()
    // disabled: fetchDataDelay is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedQuestion, pageSize, currentPage, orderBy, orderDirection])

  return [fetchData, fetchDataDelay]
}

export default useFetchDataImageResponses
